import dayjs from 'dayjs';
import type {
  CalculateCompareDateParams,
  LastSettings,
  OneDayCompareChartOption,
  ThisSettings,
} from '@/dashboard/features/widgets/oneDayCompare/settings/types/oneDayCompareSettings.types';
import {
  getYesterdayDate,
  calculateLastDay,
  calculateLastMonth,
  calculateLastWeek,
  calculateThisMonth,
  calculateThisWeek,
} from '../utils/compareDate.utils';

// compareDate.item 의 default 분기와 동일한 fallback (yesterday). compareOption 자체가
// 없을 때 calculateCompareDate 의 strict 필드 모두를 채울 필요 없이 yesterday timestamp 만
// 반환한다 — 의미가 명확하고 dead default 값을 두지 않는다. now 는 호출부가 주입한다
// (위젯은 reactive today, 그 외는 호출 시점 시각) — 자정 경계 자동 갱신을 위해 단일 소스 유지.
const getYesterdayTimestamp = (now: Date): number => getYesterdayDate(now).getTime();

// Last 케이스 날짜 계산
const calculateLastCaseDate = (now: Date, lastSettings: LastSettings): Date => {
  const { value, unit, isFixedDate, daysOfTheWeek, fixedValue, selectedDate } = lastSettings;

  switch (unit) {
    case 'day':
      return calculateLastDay(now, value);
    case 'week':
      return calculateLastWeek(now, value, daysOfTheWeek, isFixedDate);
    case 'month':
      return calculateLastMonth(now, value, isFixedDate, selectedDate, fixedValue);
    default:
      return calculateLastDay(now, value);
  }
};

// This 케이스 날짜 계산
const calculateThisCaseDate = (now: Date, thisSettings: ThisSettings): Date => {
  const { value, unit, daysOfTheWeek, selectedDate } = thisSettings;

  switch (unit) {
    case 'week':
      return calculateThisWeek(now, daysOfTheWeek);
    case 'month':
      return calculateThisMonth(now, value, selectedDate);
    default:
      return dayjs(now).startOf('day').toDate();
  }
};

export const calculateCompareDate = ({
  now,
  compareItem,
  lastSettings,
  thisSettings,
  selectedDate,
}: CalculateCompareDateParams): number => {
  switch (compareItem) {
    case 'yesterday':
      return getYesterdayDate(now).getTime();
    case 'last':
      return calculateLastCaseDate(now, lastSettings).getTime();
    case 'this':
      return calculateThisCaseDate(now, thisSettings).getTime();
    case 'compareDate':
      return dayjs(selectedDate).toDate().getTime();
    default:
      return getYesterdayDate(now).getTime();
  }
};

export const resolveCompareDateFromChartOption = (
  compareOption: OneDayCompareChartOption['compareDate'] | undefined,
  now: Date,
): number => {
  if (!compareOption) return getYesterdayTimestamp(now);
  return calculateCompareDate({
    now,
    compareItem: compareOption.item,
    lastSettings: {
      isFixedDate: compareOption.isLastFixedDate,
      daysOfTheWeek: compareOption.lastDaysOfTheWeek,
      unit: compareOption.lastUnit,
      value: compareOption.lastValue,
      selectedDate: compareOption.lastSelectedDate,
      fixedValue: compareOption.lastFixedValue,
    },
    thisSettings: {
      selectedDate: compareOption.thisSelectedDate,
      daysOfTheWeek: compareOption.thisDaysOfTheWeek,
      unit: compareOption.thisUnit,
      value: compareOption.thisValue,
    },
    selectedDate: compareOption.date,
  });
};
